import { useMemo } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { BlockLayout, SectionContentItem, SectionLayout, WidgetGridLayout } from '@trinity/chat';
import Toast from '@trinity/chat-components/components/Toast/index.js';
import type {
  SaveWidgetRequest,
  AddWidgetToSectionRequest,
  ChartWidgetApiContextValue,
} from '@trinity/chat-components/containers/ChartWidget/Context.js';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { useInvalidateChartLibraryWidgetsQuery } from '@trinity/chat-components/hooks/useInvalidateChartLibraryWidgetsQuery';

// Project-scoped adapter for `/projects/:projectId/segments*` (apps/chat-api's
// routers/v1/projects/segments.ts). Deliberately distinct from
// Project.QuantAnalysis/hooks/useChartWidgetApi.ts, which targets the unrelated
// flat `/segments` segments_v2 endpoints via `apiClient.segments`.
export const useChartWidgetApiContext = (): ChartWidgetApiContextValue => {
  const apiClient = useChatApiClient();

  return useMemo(
    () => ({
      useSaveWidget: () => {
        const invalidateChartLibraryWidgets = useInvalidateChartLibraryWidgetsQuery();
        return useMutation({
          mutationFn: (data: SaveWidgetRequest) => {
            return apiClient.projects.createProjectWidget({
              projectId: data.projectId,
              title: data.title,
              data: {
                type: data.data.type,
                props: data.data.props,
              },
              sourceMetadata: data.sourceMetadata,
            });
          },
          onSuccess: (_data, variables) => {
            invalidateChartLibraryWidgets(variables.projectId);
          },
          onError: (error: Error) => {
            Toast.error({
              title: 'Save Failed',
              body: 'There was an error saving the chart widget.',
            });
          },
        });
      },

      useGetSegments: (projectId: number, enabled = true) =>
        useQuery({
          queryKey: ['project-segments', projectId],
          queryFn: async () => {
            const response = await apiClient.projects.fetchProjectSegments({ projectId });
            return { segments: response.items.map(({ segment }) => segment) };
          },
          enabled: enabled && !!projectId,
        }),

      useAddWidgetToSection: (projectId: number) =>
        useMutation({
          mutationFn: async ({
            segmentId,
            sectionId,
            widgetId,
            insertPosition,
          }: AddWidgetToSectionRequest) => {
            const res = await apiClient.projects.fetchProjectSegmentById({ projectId, segmentId });

            const widgetItem: SectionContentItem = { type: 'widget', widgetId };

            const currentLayout: WidgetGridLayout = res.segment.dashboard ?? { blocks: [] };

            let sectionFound = false;

            const updatedDashboard = {
              blocks: currentLayout.blocks.map((block: BlockLayout) => ({
                ...block,
                sections: (block.sections ?? []).map((section: SectionLayout) => {
                  if (section.id === sectionId) {
                    sectionFound = true;
                    const existingItems = section.contentItems ?? [];
                    const newContentItems = [...existingItems];

                    if (insertPosition >= 0 && insertPosition <= newContentItems.length) {
                      newContentItems.splice(insertPosition, 0, widgetItem);
                    } else {
                      newContentItems.push(widgetItem);
                    }

                    return { ...section, contentItems: newContentItems };
                  }
                  return section;
                }),
              })),
            };

            if (!sectionFound) {
              throw new Error(`Section with ID "${sectionId}" not found in the dashboard.`);
            }

            return await apiClient.projects.updateProjectSegmentDashboard({
              projectId,
              segmentId,
              payload: updatedDashboard,
            });
          },
          onError: (error: Error) => {
            Toast.error({
              title: 'Placement Failed',
              body: 'There was an error adding the widget to the dashboard.',
            });
          },
          onSuccess: () => {
            Toast.success({
              title: 'Widget added successfully',
              body: 'The widget has been added to the selected dashboard section.',
            });
          },
        }),
    }),
    [apiClient]
  );
};
